import { UploadFile } from 'antd'

import { Stairway } from './krpano'
import { Memo } from './memo'

export type CustomUploadFile = UploadFile & {
  originalName?: string
}

type Direction = {
  id: string
  h: number
  v: number
  point_to: string
  dhgap?: number
}

export type Folders = {
  label: string
  isFileHolder: boolean
  id: number
  categoryId?: number
  children: Folders[] | null
  numberOfFile?: number
  memos?: Memo[]
}

export type FolderItem = {
  id: number
  label: string
}

export type BuildingFile = {
  id: number
  s3Path: string
  mimeType: string
  extension: string
  createdAt: string
  name: string
  category?: number
  thumbnail: string
  pointId: string | null
  takenAt: string | null
  isRelated?: boolean
  tilesUrl?: string | null
  multires?: string | null
  order?: number
  isCopy?: boolean
  initView?: InitView
  note?: string
  rawFile?: File
  size?: number
  originalName?: string
  overwriteID?: number
  mode?: 'overwrite' | 'keep'
  extraInfo?: {
    size: number | null
    page_count: number | null
    duration: number | null
    width: number | null
    height: number | null
    rasterized?: string | null
    raster_folder?: string | null
  }
  updatedAt?: string
  updatedUser?: {
    id: number
    email: string
    firstName: string
    firstNameKana: string
    lastName: string
    lastNameKana: string
  }
}

export type ListBuildingFiles = {
  files: {
    files: BuildingFile[]
    folders: { id: number; name: string; order: number }[]
  }
  pagination?: {
    page: number
    pageSize: number
    total: number
  }
}

export type BuildingChildrenFolder = {
  id: number
  name: string
  order: number
  files: BuildingFile[]
}

export type PreSignList = {
  key: string
  url: string
}

export type UploadingFiles = {
  id?: number
  uploadStatus: UploadStatus
  file: CustomUploadFile
  overwriteID?: number
  mode?: 'overwrite' | 'keep'
  abortSignal?: AbortController
}

export enum UploadStatus {
  LOADING = 'loading',
  FAIL = 'fail',
  SUCCESS = 'success',
  DEFAULT = 'default'
}

export type SelectedFiles = {
  id: number
  buildingId: string
  category: number
  files: UploadingFiles[]
}

export enum BuildingLeftMenu {
  MENU = 'menu',
  INFORMATION = 'information',
  FLOOR = 'floor',
  AREA = 'area',
  EXPORT_IMAGES = 'exportImages'
}

export type AreaDetail = {
  id: number
  name: string
  order: number
}

export type FloorDetail = {
  id: number
  name: string
  isUnregisteredMap?: boolean
  mapFile: BuildingFile
  areas: AreaDetail[]
}

export type LockedByUser = {
  id: number
  email: string
  firstName: string
  firstNameKana: string
  lastName: string
  lastNameKana: string
}

export type Point = {
  id: string
  areaId: number
  floorId: number
  order: number
  x: number
  y: number
  image360: BuildingFile | null
  annotations: Annotation[]
  initView: InitView
  directions?: Direction[]
  isLocked: boolean
  lockedByUser: LockedByUser | null
  lockedBy: number | null
  stairways?: Stairway[]
  versionId?: string
  pointVersionId?: number
  isSubImages: boolean
  subImages?: BuildingFile[]
  numberOfSubImages?: number
}

export type InitView = {
  v: number
  h: number
  z: number
  radarHGap: number
}

export type Link = {
  id: string
  point1: {
    id: string
    x: number
    y: number
  }
  point2: {
    id: string
    x: number
    y: number
  }
}

export type FloorMapElement = {
  defaultPoint: {
    areaId: number
    id: string
  } | null
  points: Point[]
  links: Link[]
}

export type Annotation = {
  id?: string
  building_id?: number
  floor_id?: number
  point_id?: string
  files?: BuildingFile[]
  name?: string
  name_occurrence?: string
  name_recovery?: string
  type?: AnnotationType
  head_h?: number
  head_v?: number
  mid_h?: number
  mid_v?: number
  h?: number
  v?: number
  link?: string
  link_occurrence?: string
  link_recovery?: string
  memo?: string
  memo_occurence_file_name?: string
  memo_occurence?: string
  memo_recovery_file_name?: string
  memo_recovery?: string
  memo_content?: string
  memo_file_name?: string
  set_annotation_id?: number
  set_annotation_name?: string
  imageID?: number | null
  memo_name?: string
  point_order?: number
  alert?: { id: number }
  links?: LinkAnnotation[]
  link_occurrences?: { link_occurrence?: string; name_occurrence?: string }[]
  link_recoveries?: { link_recovery?: string; name_recovery?: string }[]
}

export type AnnotationPayload = Omit<Annotation, 'files'> & {
  files?: number[]
}

export enum AnnotationType {
  ARCHITECTURE = 10,
  AIR_CONDITION = 20,
  ELECTRICITY = 30,
  MEMO = 40,
  DOCUMENT = 41,
  FIRE = 50,
  FLOOD = 60,
  EARTHQUAKE = 70,
  VOLCANO = 80,
  TRADITION = 90,
  EQUIPMENT = 100,
  LIFE_LINE = 110,
  IMAGE = 120,
  MOVIE = 130,
  INFO = 140
}

export type OpenModalDelete = {
  isOpen: string
  pointId: string
  pointOrder: number
}

export type LinkAnnotation = {
  link?: string
  name?: string
}

export enum BuildingStatus {
  EDIT = 10,
  PUBLIC = 20,
  PRIVATE = 30,
  IMAGE = 40,
  COMMUNITY = 50
}

export enum UserAction {
  VIEW = 'VIEW',
  EDIT = 'EDIT',
  FULL = 'FULL'
}

export type ImageFloor = {
  id: string
  order: number
  thumbnail: string
  isDefault: boolean
}

export type ListImagesFloor = {
  edges: ImageFloor[]
  pagination: {
    page: number
    pageSize: number
    total: number
  }
}

export const FileMagicNumber = {
  VIDEO: ['00014', '00018', '00020', '0001c'],
  IMAGE: ['ffd8', '89504e47'],
  PDF: ['25504446']
}

export enum DropdownMenuKey {
  HANDLE_INFORMATION = 'handleInformation',
  HANDLE_EDIT_INFORMATION = 'handleEditInformation',
  HANDLE_EDIT_STATUS = 'handleEditStatus',
  HANDLE_SHARE_BUILDING = 'handleShareBuildingModal',
  HANDLE_SETTING_DEFAULT_FLOOR = 'handleSettingDefaultFloor',
  HANDLE_EXPORT_IMAGE = 'handleExportImage',
  HANDLE_SETTING_LOGO = 'handleSettingLogo',
  HANDLE_DELETE_BUILDING = 'handleDeleteBuilding',
  HANDLE_SETTING_BUSINESS = 'handleSettingBusiness',
  HANDLE_SETTING_LOCATION = 'handleSettingLocation',
  HANDLE_ERASE_TRIPOD = 'handleEraseTripod'
}

export type FloorPointElement = {
  id: number
  name: string
  order: number
  totalPoint: number
  totalImage360: number
}

export type FloorPointList = {
  floors: FloorPointElement[]
  totalPointBuilding: number
  totalImage360Building: number
}

export type RelatedPoint = {
  floorId: number
  floor_id?: number
  name: string
  order: number
  point: {
    id: string
    areaId: number
    areaName: string
    x: number
    y: number
    order: number
    image360: BuildingFile | null
    initView: InitView
  }
}

export interface ListRelatedPoints {
  pagination: {
    page: number
    pageSize: number
    total: number
  }
  edges: RelatedPoint[]
}

export type PointHistory = {
  editedAt: string
  user: {
    email: string
    firstName: string
    firstNameKana: string
    id: number
    isDeleted: boolean
    lastName: string
    lastNameKana: string
  }
  versionId: string
  timePeriod?: string
  isCurrentYear?: boolean
  isWithinWeek?: boolean
}

export type PointHistoryList = {
  edges: PointHistory[]
  pagination: {
    page: number
    pageSize: number
    total: number
  }
}

export type BuildingCSV = {
  id: number
  name: string
  organizationName: string
  status: number
  branchName: string
  branchStoreName: string
  createdAt: string
  latestUpdated: string
  designerName: string
  architectName: string
  constructionPeriodStart: string
  constructionPeriodEnd: string
  siteArea: string
  buildingArea: string
  totalFloorArea: string
  structure: string
  floorNumber: string
  code: string
  aliasName: string
  zipcode: string
  prefCode: number
  cityName: string
  streetName: string
  buildingName: string
  isDeleted: boolean
  fileCount: number
}

export type SetAnnotation = {
  id?: number
  link_occurrence?: string
  link_recovery?: string
  memo: string
  memo_file_name?: string
  memo_occurence?: string
  memo_occurence_file_name?: string
  memo_recovery?: string
  memo_recovery_file_name?: string
  type: AnnotationType
  name: string
  link_name_occurrence?: string
  link_name_recovery?: string
  files?: BuildingFile[]
  set_annotation_name?: string
  links?: LinkAnnotation[]
  link_occurrences?: { link_occurrence: string; name_occurrence: string }[]
  link_recoveries?: { link_recovery: string; name_recovery: string }[]
  memo_name?: string
}

export enum BuildingCopyStatus {
  BUILDING_SOURCE = -1,
  PENDING = 0,
  PROCESSING = 1,
  SUCCESS = 2,
  FAILED = 3
}

export type CustomFolder = {
  id: number
  categoryId: number
  name: string
  order: number
  createdAt: string
  updatedAt: string
  childFolders: {
    id: number
    categoryId: number
    name: string
    order: number
    createdAt: string
    updatedAt: string
    parentId: number
  }[]
}

export type SenceData = {
  id: number
  tilesUrl: string
  multires: string
  s3Path: string
  thumbnailUrl: string
}
export type SenceDataResponse = {
  code: number
  data: SenceData[]
}
export type SearchFile = {
  id: number
  name: string
  thumbnail: string
  mimeType: string
  extension: string
  pointId: string | null
  takenAt: string | null
  createdAt: string
  updatedAt: string
  isRelated?: boolean
  extraInfo?: {
    size: number | null
    page_count: number | null
    duration: number | null
    width: number | null
    height: number | null
  }
}

export type SearchFolder = {
  id: number
  name: string
  order: number
  files: SearchFile[]
  memos?: Memo[]
}

export type SearchCategoryChild = {
  id: number
  categoryId: number
  label: string
  isFileHolder: boolean
  files: SearchFile[]
  folder: SearchFolder[]
  memos?: Memo[]
  children?: SearchCategoryChild[] | string
}

export type SearchCategoryGroup = {
  id: number
  categoryId: number
  label: string
  isFileHolder: boolean
  isCustomFolder: boolean
  files: SearchFile[]
  folder: SearchFolder[]
  memos?: Memo[]
  children: SearchCategoryChild[]
}

export type SearchResponse = {
  code: number
  data: {
    category: SearchCategoryGroup
  }[]
  pagination?: {
    page: number
    pageSize: number
    total: number
  }
}

export type BuildingSessionDeviceUser = {
  id: number
  firstName: string
  lastName: string
}

export type BuildingSessionDevice = {
  id: number
  deviceId: string
  deviceName?: string
  status: string
  startedAt: string
  user?: BuildingSessionDeviceUser
}
